import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { followAgent } from "./agent-entrances";
import { MAKE_3D_EVENT } from "./MakeThreeD";
import { whenEditsSaved } from "./canvas/sync-client";
import { MAKE_3D_REQUEST } from "../shared/make-3d";
import { SceneViewer } from "./SceneViewer";
import { commentOnPlan as commentOnSceneSource } from "./SceneShape";
import type { ScenePick } from "./scene-engine";
import type { SceneSpec } from "../shared/scene-spec";
import { useValue } from "@tldraw/state-react";
import {
  ArrowUp,
  ArchiveRestore,
  ChevronLeft,
  Eye,
  Locate,
  Plus,
  Redo2,
  Sparkles,
  Square,
  Undo2,
  X,
} from "lucide-react";
import type { TLRecord, TLShape, TLShapeId } from "@tldraw/tlschema";
import type { Editor } from "./canvas";
import type { Room } from "./api";
import { api } from "./api";
import { Modal } from "./Modal";
import {
  ConversationMenu,
  type LifecycleAction,
} from "./AgentConversationMenu";
import {
  nextAgentSlot,
  recordLabel,
  type AgentDraft,
  type DraftSummary,
} from "../shared/agent-drafts";
import {
  AgentMark,
  agentName,
  markState,
  setLocalAgentsAvailable,
  useAgentStyle,
} from "./AgentIdentity";
import type { AgentStyle } from "./preferences";
import {
  AgentActivity,
  AgentResourceCards,
  AgentText,
  describeChange,
  type ChangeSummary,
} from "./AgentResources";
import { publishAgentThreads } from "./agent-threads";
import "./local-agents.css";

type Runtime = {
  enabled: boolean;
  available: boolean;
  signedIn: boolean;
  provider?: string;
  model?: string;
  error?: string;
};
type View = "closed" | "list" | "compose" | "thread";

const statusText = (s: DraftSummary["status"]) =>
  ({
    new: "New",
    working: "Working…",
    waiting: "Needs your answer",
    ready: "Done",
    stopped: "Stopped",
    failed: "Needs attention",
    applied: "Done",
  })[s];

/** Short starters; picking one fills the composer with the full request. */
const suggestions = [
  {
    label: "Fix label typos",
    prompt: "Rewrite the room labels in title case and fix any typos.",
  },
  {
    label: "Make an options sheet",
    prompt:
      "Lay out an A3 options sheet comparing two kitchen arrangements, with notes.",
  },
  {
    label: "Find reference photos",
    prompt:
      "Find three reference photos of brick courtyards and add them beside the plan.",
  },
];

function relativeTime(at: number) {
  const minutes = Math.round((Date.now() - at) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return new Date(at).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });
}

/** One agent reply with the canvas changes, resources and steps of its turn. */
type Turn = {
  user: AgentDraft["messages"][number];
  agent: AgentDraft["messages"][number][];
  changes: ChangeSummary[];
  resources: NonNullable<AgentDraft["resources"]>;
  calls: NonNullable<AgentDraft["toolCalls"]>;
  runs: NonNullable<AgentDraft["runs"]>;
  /** Indices of this turn's edits in the draft's undo history. */
  applied: number[];
  live: boolean;
};
function turnsOf(draft: AgentDraft): Turn[] {
  const turns: Turn[] = [];
  for (const m of draft.messages) {
    if (m.role === "user" || !turns.length)
      turns.push({
        user: m,
        agent: [],
        changes: [],
        resources: [],
        calls: [],
        runs: [],
        applied: [],
        live: false,
      });
    if (m.role === "agent") turns.at(-1)!.agent.push(m);
  }
  turns.forEach((turn, i) => {
    const from = turn.user.at,
      to = turns[i + 1]?.user.at ?? Infinity;
    const within = (at: number) => at >= from && at < to;
    const seen = new Set<string>();
    for (const step of draft.steps)
      if (!step.kind && within(step.at))
        for (const [id, change] of Object.entries(step.changes)) {
          const summary = describeChange(id, change);
          if (summary && !seen.has(id)) {
            seen.add(id);
            turn.changes.push(summary);
          }
        }
    turn.resources = (draft.resources ?? []).filter((r) => within(r.at));
    turn.calls = (draft.toolCalls ?? []).filter((c) => within(c.at));
    turn.runs = (draft.runs ?? []).filter((r) => within(r.started));
    (draft.applications ?? []).forEach((a, k) => {
      if (within(a.at)) turn.applied.push(k);
    });
    turn.live =
      draft.status === "working" &&
      i === turns.length - 1 &&
      !turn.agent.length;
  });
  return turns;
}

/** Dashed outlines over objects the agent is reading or may change; screen only, never exported. */
function Outlines({
  editor,
  ids,
  label,
}: {
  editor: Editor;
  ids: string[];
  label?: string;
}) {
  const key = ids.slice(0, 20).join();
  const boxes = useValue(
    "agent outlines",
    () =>
      key
        .split(",")
        .filter(Boolean)
        .flatMap((id) => {
          const shape = editor.getShape(id as TLShapeId);
          const b = shape && editor.getShapePageBounds(shape);
          if (!b) return [];
          const a = editor.pageToScreen({ x: b.x, y: b.y }),
            z = editor.pageToScreen({ x: b.x + b.w, y: b.y + b.h });
          return [
            { id, left: a.x, top: a.y, width: z.x - a.x, height: z.y - a.y },
          ];
        }),
    [editor, key],
  );
  return (
    <>
      {boxes.map(({ id, ...box }, i) => (
        <div key={id} className="agent-outline" style={box} aria-hidden>
          {i === 0 && label && <span>{label}</span>}
        </div>
      ))}
    </>
  );
}

/** Scope IDs without their children: the objects a person would name. */
function topLevel(draft: AgentDraft) {
  const scope = new Set(draft.scopeIds);
  return draft.scopeIds.filter((id) => {
    const r = draft.head[id];
    return r?.typeName === "shape" && !scope.has(r.parentId);
  });
}

function Presence({
  editor,
  draft,
  style,
  open,
}: {
  editor: Editor;
  draft: DraftSummary;
  style: AgentStyle;
  open: () => void;
}) {
  const bounds = useValue(
    "agent context",
    () => {
      const shape = draft.scopeIds
        .map((id) => editor.getShape(id as TLShapeId))
        .find(Boolean);
      if (!shape) return null;
      const b = editor.getShapePageBounds(shape);
      if (!b) return null;
      const p = editor.pageToScreen({ x: b.x + b.w, y: b.y });
      return { left: p.x + 6, top: p.y };
    },
    [editor, draft.scopeIds],
  );
  if (
    !bounds ||
    !["working", "waiting"].includes(draft.status) ||
    bounds.left > innerWidth - 120 ||
    bounds.top < 70 ||
    bounds.top > innerHeight - 80
  )
    return null;
  return (
    <button
      className={`agent-presence ${draft.status}`}
      style={bounds}
      onClick={open}
    >
      <AgentMark
        style={style}
        draft={draft}
        state={markState(draft.status)}
        size={18}
      />
      {draft.status === "working"
        ? `${agentName(style, draft)} is working…`
        : `${agentName(style, draft)} has a question`}
    </button>
  );
}

export function LocalAgents({
  editor,
  room,
  connected,
  userId,
  importFiles,
  commentAt,
}: {
  editor: Editor;
  room: Room;
  connected: boolean;
  userId: string;
  /** The workspace's ordinary file import, used to place a scene as a 3D model. */
  importFiles?: (files: File[], point?: { x: number; y: number }) => void;
  /** Open the comment composer at a shape-local point. */
  commentAt?: (shape: TLShape, point: { x: number; y: number }) => void;
}) {
  const style = useAgentStyle(userId);
  const [scene, setScene] = useState<SceneSpec | null>(null);
  const closeScene = useCallback(() => setScene(null), []);
  async function addScene(spec: SceneSpec) {
    if (!importFiles) throw new Error("Adding to the board is unavailable here.");
    const { sceneToGlb } = await import("./scene-engine");
    const file = await sceneToGlb(spec);
    const sheet = spec.plan && editor.getShape(spec.plan.sheetId as TLShapeId);
    const bounds = sheet && editor.getShapePageBounds(sheet);
    if (sheet) editor.setCurrentPage(editor.getAncestorPageId(sheet)!);
    importFiles(
      [file],
      bounds ? { x: bounds.x + bounds.w + 320, y: bounds.y + bounds.h / 2 } : undefined,
    );
  }
  function commentOnPlan(spec: SceneSpec, pick: ScenePick) {
    if (!commentAt) return;
    setScene(null);
    const problem = commentOnSceneSource(editor, spec, pick, commentAt);
    if (problem) setError(problem);
  }
  const [runtime, setRuntime] = useState<Runtime | null>(null);
  const [list, setList] = useState<DraftSummary[]>([]);
  const [view, setView] = useState<View>("closed");
  const [active, setActive] = useState<string | null>(null);
  const [draft, setDraft] = useState<AgentDraft | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [connectionError, setConnectionError] = useState("");
  const [busy, setBusy] = useState(false);
  const [archived, setArchived] = useState(false);
  const [deleteConversation, setDeleteConversation] =
    useState<DraftSummary | null>(null);
  const [notice, setNotice] = useState("");
  const body = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const current = useRef({ id: active, version: draft?.version ?? -1 });
  const refreshVersion = useRef(0);
  current.current = { id: active, version: draft?.version ?? -1 };
  const selection = useValue(
    "agent selection",
    () => editor.getSelectedShapeIds(),
    [editor],
  );
  const selectionLabel = useValue(
    "agent selection label",
    () => {
      const ids = editor.getSelectedShapeIds();
      if (!ids.length) return "Whole project";
      const labels = ids
        .map((id) => editor.getShape(id))
        .filter(Boolean)
        .map((s) => recordLabel(s as TLRecord));
      return ids.length <= 2
        ? labels.join(", ")
        : `${labels[0]} and ${ids.length - 1} more`;
    },
    [editor],
  );
  const [showFocus, setShowFocus] = useState(false);
  const nameOf = (id: string) => {
    const record = draft?.head[id];
    return record ? recordLabel(record) : undefined;
  };
  // What the agent may change: the selection before sending, then the conversation's scope.
  const focusIds: string[] =
    view === "compose" || !draft ? selection : topLevel(draft);
  const focusLabel = (() => {
    if (!focusIds.length) return "only what it adds";
    if (view === "compose" || !draft) return selectionLabel;
    const names = focusIds.map((id) => nameOf(id) ?? "object");
    return names.length <= 2
      ? names.join(", ")
      : `${names[0]} and ${names.length - 1} more`;
  })();
  // What it is reading or looking at right now, outlined on the canvas.
  const reading = (() => {
    const call =
      draft?.status === "working"
        ? draft.toolCalls?.find((c) => c.state === "running")
        : undefined;
    const input = (call?.input ?? {}) as {
      sheet?: string;
      request?: { action?: string; ids?: string[] };
    };
    if (call?.name === "read_canvas" && input.sheet) {
      const sheet = Object.values(draft!.head).find(
        (r) =>
          r.typeName === "shape" &&
          r.type === "sheet" &&
          (r.id === input.sheet ||
            recordLabel(r).toLowerCase() === input.sheet!.toLowerCase()),
      );
      return sheet ? { ids: [sheet.id as string], label: "Reading" } : null;
    }
    if (call?.name === "workspace_tool" && input.request?.action === "render")
      return { ids: input.request.ids ?? [], label: "Looking" };
    return null;
  })();
  const endpoint = `/api/rooms/${room.id}/agent-drafts`;
  const refresh = useCallback(async () => {
    const version = ++refreshVersion.current;
    const selectedId = current.current.id;
    const rows = await api<DraftSummary[]>(endpoint);
    if (version !== refreshVersion.current) return;
    setList(rows);
    if (current.current.id !== selectedId) return;
    const selected = rows.find((r) => r.id === selectedId);
    if (current.current.id && !selected) {
      current.current = { id: null, version: -1 };
      setActive(null);
      setDraft(null);
      setView((v) => (v === "thread" ? "list" : v));
    }
    if (selected && selected.version !== current.current.version) {
      const next = await api<AgentDraft>(`${endpoint}/${selected.id}`);
      if (current.current.id === next.id) setDraft(next);
    }
  }, [endpoint]);
  useEffect(() => {
    let disposed = false;
    void api<Runtime>("/api/local-agent-runtime")
      .then((r) => {
        if (disposed) return;
        setRuntime(r);
        setLocalAgentsAvailable(r.enabled);
      })
      .catch((e) => {
        if (!disposed) setError((e as Error).message);
      });
    return () => {
      disposed = true;
      setLocalAgentsAvailable(false);
    };
  }, []);
  useEffect(() => {
    // Comment threads read the same poll for @Agent status; they hide it once this unmounts.
    publishAgentThreads({
      enabled: !!runtime?.enabled && !!runtime.available,
      endpoint,
      list,
    });
  }, [runtime?.enabled, runtime?.available, endpoint, list]);
  useEffect(
    () => () => publishAgentThreads({ enabled: false, endpoint: "", list: [] }),
    [],
  );
  useEffect(() => {
    if (!runtime?.enabled) return;
    let disposed = false,
      timer: number;
    const poll = async () => {
      try {
        if (!disposed) {
          await refresh();
          setConnectionError("");
        }
      } catch (e) {
        if (!disposed) setConnectionError((e as Error).message);
      }
      if (!disposed) timer = window.setTimeout(() => void poll(), 1000);
    };
    void poll();
    return () => {
      disposed = true;
      clearTimeout(timer);
    };
  }, [runtime?.enabled, refresh]);
  useEffect(() => {
    if (view === "closed") return;
    const escape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setView("closed");
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", escape);
    return () => window.removeEventListener("keydown", escape);
  }, [view]);
  const messageCount = draft?.messages.length ?? 0,
    callCount = draft?.toolCalls?.length ?? 0;
  useEffect(() => {
    if (view === "thread" && body.current)
      body.current.scrollTop = body.current.scrollHeight;
  }, [messageCount, callCount, draft?.status, view]);
  useEffect(() => {
    if (view === "compose" || view === "thread") input.current?.focus();
  }, [view, draft?.id]);
  useEffect(() => {
    const area = input.current;
    if (!area) return;
    area.style.height = "auto";
    area.style.height = `${Math.min(area.scrollHeight, 132)}px`;
  }, [message, view]);

  function focusRecord(record: TLRecord) {
    let id = record.id as string;
    if (record.typeName === "comment")
      id = draft?.head[record.threadId]?.id ?? id;
    const thread = draft?.head[id];
    if (thread?.typeName === "comment-thread" && thread.anchor.type === "shape")
      id = thread.anchor.shapeId;
    const shape = editor.getShape(id as TLShapeId);
    if (!shape) {
      setError("That object is no longer on the canvas.");
      return;
    }
    setError("");
    editor.setCurrentPage(editor.getAncestorPageId(shape)!);
    editor.select(shape.id);
    const bounds = editor.getShapePageBounds(shape);
    if (bounds) {
      const viewport = editor.getViewportScreenBounds();
      const reserved = viewport.w > 760 ? 420 : 0;
      const zoom = Math.max(
        0.05,
        Math.min(
          1.5,
          (viewport.w - reserved - 120) / Math.max(bounds.w, 1),
          (viewport.h - 220) / Math.max(bounds.h, 1),
        ),
      );
      editor.zoomToBounds(bounds, { targetZoom: zoom, immediate: true });
      if (reserved) {
        const camera = editor.getCamera();
        editor.setCamera({
          ...camera,
          x: camera.x - reserved / (2 * camera.z),
        });
      }
    }
  }
  async function openThread(id: string) {
    setActive(id);
    current.current = { id, version: -1 };
    setDraft(null);
    setView("thread");
    setError("");
    setMessage("");
    try {
      const next = await api<AgentDraft>(`${endpoint}/${id}`);
      if (current.current.id === id) setDraft(next);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  function compose() {
    setActive(null);
    setDraft(null);
    setView("compose");
    setError("");
    setMessage("");
    setNotice("");
  }
  function toggle() {
    if (view !== "closed") {
      setView("closed");
      return;
    }
    setError("");
    if (active) void openThread(active);
    else if (list.length) setView("list");
    else compose();
  }
  async function send() {
    const text = message.trim();
    if (!text || busy) return;
    setBusy(true);
    setError("");
    try {
      await whenEditsSaved();
      let id = draft?.id;
      if (!id) {
        const created = await api<AgentDraft>(endpoint, {
          selectedIds: selection,
        });
        id = created.id;
        setActive(id);
        current.current = { id, version: -1 };
        setView("thread");
      }
      followAgent(id);
      const next = await api<AgentDraft>(`${endpoint}/${id}/message`, {
        message: text,
      });
      if (current.current.id === next.id) setDraft(next);
      setMessage("");
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  /** One click from a drawing: a new conversation scoped to it, with the fixed request. */
  async function makeThreeD(shapeId: string) {
    if (busy) return;
    setBusy(true);
    setError("");
    setView("thread");
    try {
      // A scale set a moment ago must reach the server before the agent reads the board.
      await whenEditsSaved();
      const created = await api<AgentDraft>(endpoint, { selectedIds: [shapeId] });
      setActive(created.id);
      current.current = { id: created.id, version: -1 };
      setDraft(created);
      followAgent(created.id);
      const next = await api<AgentDraft>(`${endpoint}/${created.id}/message`, {
        message: MAKE_3D_REQUEST,
      });
      if (current.current.id === next.id) setDraft(next);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const makeThreeDRef = useRef(makeThreeD);
  makeThreeDRef.current = makeThreeD;
  useEffect(() => {
    const onMake = (event: Event) =>
      void makeThreeDRef.current((event as CustomEvent<{ shapeId: string }>).detail.shapeId);
    window.addEventListener(MAKE_3D_EVENT, onMake);
    return () => window.removeEventListener(MAKE_3D_EVENT, onMake);
  }, []);
  async function action(name: "stop" | "undo" | "redo") {
    if (!draft) return;
    setBusy(true);
    setError("");
    try {
      if (name === "redo") followAgent(draft.id, 15_000);
      const next = await api<AgentDraft>(`${endpoint}/${draft.id}/${name}`, {});
      if (current.current.id === next.id) setDraft(next);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  /** Allow the objects the agent asked to change, then let it carry on. */
  async function allowAndContinue() {
    if (!draft || busy) return;
    setBusy(true);
    setError("");
    try {
      await api<AgentDraft>(`${endpoint}/${draft.id}/allow`, {});
      const next = await api<AgentDraft>(`${endpoint}/${draft.id}/message`, {
        message: "Allowed. Go ahead.",
      });
      if (current.current.id === next.id) setDraft(next);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  function editRequest(
    draft: AgentDraft,
    message: AgentDraft["messages"][number] | undefined,
  ) {
    if (!message?.editIds?.length) return null;
    const pending = !message.allowed && draft.messages.at(-1) === message;
    const objects = message.editIds.flatMap((id) => {
      const record = draft.head[id];
      const summary =
        record && describeChange(id, { before: record, after: record });
      return record && summary ? [{ id, record, summary }] : [];
    });
    return (
      <div className="agent-edit-request">
        <small>
          {message.allowed
            ? "You allowed changes to"
            : pending
              ? "Needs your OK to change"
              : "Asked to change"}
        </small>
        <ul className="agent-changes">
          {objects.slice(0, 6).map(({ id, record, summary }) => (
            <li key={id}>
              <button
                onClick={() => focusRecord(record)}
                title={`Show on canvas${summary.label ? ` · ${summary.label}` : ""}`}
              >
                <Locate size={12} />
                {summary.noun}
                {summary.label && <em>{summary.label}</em>}
              </button>
            </li>
          ))}
          {objects.length > 6 && (
            <li className="agent-more">+{objects.length - 6} more</li>
          )}
        </ul>
        {pending && (
          <div className="agent-turn-actions">
            <button
              onClick={() => void allowAndContinue()}
              disabled={busy || draft.status === "working"}
            >
              Allow and continue
            </button>
          </div>
        )}
      </div>
    );
  }

  async function manage(conversation: DraftSummary, name: LifecycleAction) {
    if (busy) return;
    if (name === "delete" && deleteConversation?.id !== conversation.id) {
      setError("");
      setDeleteConversation(conversation);
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api(
        `${endpoint}/${conversation.id}/${name}`,
        name === "delete" ? { confirm: true } : {},
      );
      if (current.current.id === conversation.id) {
        current.current = { id: null, version: -1 };
        setActive(null);
        setDraft(null);
        setMessage("");
      }
      setArchived(name === "restore" ? false : archived);
      setView("list");
      setDeleteConversation(null);
      setNotice(
        name === "delete"
          ? "Conversation deleted. Canvas edits are kept."
          : name === "archive"
            ? "Conversation archived. Find it in Archived."
            : "Conversation restored.",
      );
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const turns = useMemo(() => (draft ? turnsOf(draft) : []), [draft]);
  if (!runtime?.enabled) return null;
  const busyAgents = list
    .filter(
      (d) =>
        !d.archivedAt && (d.status === "working" || d.status === "waiting"),
    )
    .slice(0, 3);
  const activeList = list.filter((d) => !d.archivedAt);
  const visibleList = list.filter((d) => Boolean(d.archivedAt) === archived);
  const next = { id: "next", agent: nextAgentSlot(activeList) };
  const history = draft?.applications ?? [];
  const cursor = draft?.historyCursor ?? history.length;
  const configured = runtime.available && runtime.signedIn;
  const canSend =
    !!message.trim() &&
    !busy &&
    connected &&
    configured &&
    !draft?.archivedAt &&
    draft?.status !== "working";
  const title =
    view === "list"
      ? "Agent"
      : view === "compose"
        ? "New conversation"
        : (draft?.title ?? "Opening…");
  const subtitle =
    view === "list"
      ? `${visibleList.length} ${archived ? "archived " : ""}conversation${visibleList.length === 1 ? "" : "s"}`
      : view === "compose"
        ? `${agentName(style, next)} · ${selectionLabel}`
        : draft
          ? [
              agentName(style, draft),
              draft.archivedAt
                ? "Archived"
                : !["new", "ready", "applied"].includes(draft.status) &&
                  statusText(draft.status),
              draft.contextLabel,
            ]
              .filter(Boolean)
              .join(" · ")
          : "";
  return (
    <>
      <button
        className={`agent-trigger floating-surface ${view !== "closed" ? "open" : ""}`}
        aria-expanded={view !== "closed"}
        aria-label={
          view !== "closed"
            ? "Close agent"
            : selection.length
              ? `Agent · ${selection.length} selected object${selection.length === 1 ? "" : "s"}`
              : "Agent · whole project"
        }
        title={
          view !== "closed"
            ? "Close"
            : selection.length
              ? `Ask the agent about ${selectionLabel}`
              : "Ask the agent about the whole project"
        }
        onClick={toggle}
      >
        <Sparkles size={15} />
        <span>Agent</span>
        {busyAgents.length > 0 && (
          <span
            className="agent-trigger-marks"
            aria-label={busyAgents
              .map(
                (d) =>
                  `${agentName(style, d)} ${d.status === "working" ? "is working" : "needs your answer"}`,
              )
              .join(", ")}
          >
            {busyAgents.map((d) => (
              <AgentMark
                key={d.id}
                style={style}
                draft={d}
                state={markState(d.status)}
                size={20}
              />
            ))}
          </span>
        )}
      </button>
      {view !== "closed" && reading && (
        <Outlines editor={editor} ids={reading.ids} label={reading.label} />
      )}
      {view !== "closed" && !reading && showFocus && (
        <Outlines editor={editor} ids={focusIds} />
      )}
      {activeList.map((d) => (
        <Presence
          key={d.id}
          editor={editor}
          draft={d}
          style={style}
          open={() => void openThread(d.id)}
        />
      ))}
      {view !== "closed" && (
        <section
          className="agent-panel floating-surface"
          aria-label="Agent"
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === "Escape") setView("closed");
          }}
        >
          <header>
            {view !== "list" && list.length > 0 && (
              <button
                aria-label="All conversations"
                title="All conversations"
                onClick={() => setView("list")}
              >
                <ChevronLeft size={17} />
              </button>
            )}
            {view === "thread" && draft && (
              <AgentMark
                style={style}
                draft={draft}
                state={markState(draft.status)}
                size={30}
              />
            )}
            {view === "compose" && (
              <AgentMark style={style} draft={next} state="idle" size={30} />
            )}
            <div>
              <strong>{title}</strong>
              <small aria-live="polite" title={subtitle}>
                {subtitle}
              </small>
            </div>
            {view === "thread" && draft && (
              <ConversationMenu
                conversation={draft}
                disabled={busy || !connected}
                act={(name) => void manage(draft, name)}
              />
            )}
            {view !== "compose" && (
              <button
                aria-label="New conversation"
                title={
                  selection.length
                    ? `New conversation about ${selectionLabel}`
                    : "New conversation about the whole project"
                }
                onClick={compose}
              >
                <Plus size={17} />
              </button>
            )}
            <button aria-label="Close" onClick={() => setView("closed")}>
              <X size={17} />
            </button>
          </header>
          {view === "list" && (
            <nav className="agent-list-tabs" aria-label="Conversations">
              <button
                aria-pressed={!archived}
                onClick={() => {
                  setArchived(false);
                  setNotice("");
                }}
              >
                Active <small>{activeList.length}</small>
              </button>
              <button
                aria-pressed={archived}
                onClick={() => {
                  setArchived(true);
                  setNotice("");
                }}
              >
                Archived <small>{list.length - activeList.length}</small>
              </button>
            </nav>
          )}
          <div className="agent-body" ref={body}>
            {view === "list" && (
              <div className="agent-list">
                {visibleList.map((d) => (
                  <div key={d.id} className="agent-list-row">
                    <button
                      className={`agent-list-open ${d.status}`}
                      onClick={() => void openThread(d.id)}
                    >
                      <AgentMark
                        style={style}
                        draft={d}
                        state={markState(d.status)}
                        size={32}
                      />
                      <span className="agent-list-text">
                        <strong>{d.title}</strong>
                        <small>
                          <span>
                            {agentName(style, d)} · {statusText(d.status)}
                          </span>
                          <span>{d.contextLabel}</span>
                          <time>{relativeTime(d.updated)}</time>
                        </small>
                      </span>
                    </button>
                    <ConversationMenu
                      conversation={d}
                      disabled={busy || !connected}
                      act={(name) => void manage(d, name)}
                    />
                  </div>
                ))}
                {!visibleList.length && (
                  <p className="agent-intro">
                    {archived
                      ? "No archived conversations."
                      : "No active conversations. Start a new conversation or restore one from Archived."}
                  </p>
                )}
              </div>
            )}
            {view === "compose" && (
              <ul className="agent-suggestions">
                {suggestions.map((s) => (
                  <li key={s.label}>
                    <button
                      title={s.prompt}
                      onClick={() => {
                        setMessage(s.prompt);
                        input.current?.focus();
                      }}
                    >
                      {s.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {view === "thread" &&
              turns.map((turn, i) => {
                const undone =
                  turn.applied.length > 0 && cursor <= turn.applied[0];
                const canUndo = turn.applied.includes(cursor - 1);
                const canRedo = turn.applied.includes(cursor);
                const locked = busy || draft?.status === "working";
                return (
                  <article className="agent-turn" key={turn.user.at + i}>
                    {turn.user.role === "user" && (
                      <p className="agent-message user">
                        <AgentText text={turn.user.text} />
                      </p>
                    )}
                    {turn.live && draft && (
                      <div className="agent-live" aria-live="polite">
                        <AgentMark
                          style={style}
                          draft={draft}
                          state="working"
                          size={18}
                        />
                        <AgentActivity
                          calls={turn.calls}
                          runs={turn.runs}
                          live
                          nameOf={nameOf}
                        />
                      </div>
                    )}
                    {!turn.live && (
                      <AgentActivity
                        calls={turn.calls}
                        runs={turn.runs}
                        live={false}
                        nameOf={nameOf}
                      />
                    )}
                    {turn.agent.map((m, j) => (
                      <p className="agent-message agent" key={j}>
                        <AgentText text={m.text} />
                      </p>
                    ))}
                    {draft && editRequest(draft, turn.agent.at(-1))}
                    {!turn.live && (
                      <AgentResourceCards
                        resources={turn.resources}
                        openScene={setScene}
                        showShape={(id) => {
                          const shape = editor.getShape(id as TLShapeId);
                          if (shape) focusRecord(shape as TLRecord);
                          else setError("That 3D model is no longer on the canvas.");
                        }}
                        hasShape={(id) => !!editor.getShape(id as TLShapeId)}
                      />
                    )}
                    {!turn.live && turn.changes.length > 0 && (
                      <ul className={`agent-changes ${undone ? "undone" : ""}`}>
                        {turn.changes.slice(0, 6).map((c) => (
                          <li key={c.id}>
                            <button
                              onClick={() => focusRecord(c.record)}
                              title={`Show on canvas${c.label ? ` · ${c.label}` : ""}`}
                            >
                              <Locate size={12} />
                              {c.verb} {c.noun}
                              {c.label && <em>{c.label}</em>}
                            </button>
                          </li>
                        ))}
                        {turn.changes.length > 6 && (
                          <li className="agent-more">
                            +{turn.changes.length - 6} more
                          </li>
                        )}
                      </ul>
                    )}
                    {(canUndo || canRedo || undone) && (
                      <div className="agent-turn-actions">
                        {undone && <small>Undone</small>}
                        {canUndo && (
                          <button
                            onClick={() => void action("undo")}
                            disabled={locked}
                            title="Undo these changes on the canvas"
                          >
                            <Undo2 size={13} /> Undo
                          </button>
                        )}
                        {canRedo && (
                          <button
                            onClick={() => void action("redo")}
                            disabled={locked}
                            title="Put these changes back on the canvas"
                          >
                            <Redo2 size={13} /> Redo
                          </button>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            {view === "thread" && draft && !draft.messages.length && (
              <p className="agent-intro">Tell the agent what to change.</p>
            )}
          </div>
          {notice && (
            <p className="agent-notice" role="status">
              {notice}
            </p>
          )}
          {(error || connectionError || draft?.error) && (
            <p className="agent-error" role="alert">
              {error || connectionError || draft?.error}
            </p>
          )}
          {!configured && (
            <p className="agent-error">
              {runtime.error ??
                (runtime.provider === "codex"
                  ? "Sign in with codex login in your terminal, then reload."
                  : "Configure an agent provider on the server, then reload.")}
            </p>
          )}
          {view !== "list" && draft?.archivedAt && (
            <footer>
              <div className="agent-archived-note">
                <span>This conversation is archived.</span>
                <button
                  disabled={busy || !connected}
                  onClick={() => void manage(draft, "restore")}
                >
                  <ArchiveRestore size={14} /> Restore
                </button>
              </div>
            </footer>
          )}
          {view !== "list" && !draft?.archivedAt && (
            <footer>
              <button
                type="button"
                className="agent-sees"
                title="The agent can read every sheet. It changes only these objects and what it adds, and asks before changing anything else."
                onMouseEnter={() => setShowFocus(true)}
                onMouseLeave={() => setShowFocus(false)}
                onFocus={() => setShowFocus(true)}
                onBlur={() => setShowFocus(false)}
                onClick={() => {
                  const first =
                    focusIds[0] && editor.getShape(focusIds[0] as TLShapeId);
                  if (first) focusRecord(first);
                }}
              >
                <Eye size={12} />
                <span>Sees the whole project · changes {focusLabel}</span>
              </button>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void send();
                }}
              >
                <textarea
                  ref={input}
                  rows={1}
                  aria-label="Message agent"
                  placeholder={
                    draft?.status === "waiting"
                      ? "Answer the agent…"
                      : draft?.status === "working"
                        ? "Working…"
                        : view === "compose"
                          ? selection.length
                            ? "What should change here?"
                            : "What should the agent do?"
                          : "Follow up…"
                  }
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      !e.shiftKey &&
                      !e.nativeEvent.isComposing
                    ) {
                      e.preventDefault();
                      void send();
                    }
                  }}
                  maxLength={4000}
                  disabled={draft?.status === "working" || !connected}
                />
                {draft?.status === "working" ? (
                  <button
                    type="button"
                    className="agent-stop"
                    aria-label="Stop agent"
                    title="Stop"
                    onClick={() => void action("stop")}
                  >
                    <Square size={13} />
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="agent-send"
                    aria-label="Send"
                    title="Send (Enter)"
                    disabled={!canSend}
                  >
                    <ArrowUp size={16} />
                  </button>
                )}
              </form>
            </footer>
          )}
        </section>
      )}
      {deleteConversation && (
        <Modal
          title="Delete conversation?"
          initialFocus=".agent-delete-cancel"
          close={() => {
            if (!busy) setDeleteConversation(null);
          }}
        >
          <p>
            Delete <strong>{deleteConversation.title}</strong>? You won’t be
            able to reopen this conversation or use its agent Undo/Redo
            controls. Its canvas edits stay in the project.
          </p>
          <p>Archive it to keep the conversation for later.</p>
          {error && <p role="alert">{error}</p>}
          <div className="dialog-actions">
            <button
              className="agent-delete-cancel"
              disabled={busy}
              onClick={() => setDeleteConversation(null)}
            >
              Cancel
            </button>
            <button
              disabled={busy || !connected}
              onClick={() => void manage(deleteConversation, "delete")}
            >
              {busy ? "Deleting…" : "Delete conversation"}
            </button>
          </div>
        </Modal>
      )}
      {scene && (
        <SceneViewer
          scene={scene}
          close={closeScene}
          addToBoard={importFiles && connected ? addScene : undefined}
          commentOnPlan={commentAt ? commentOnPlan : undefined}
        />
      )}
    </>
  );
}
